import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axios';
import { DashboardLayout } from '../common/page_component/index.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import DocumentModal from '../common/DocumentModal';
import Alert from '../common/Alert';
import ShipmentTrackingPanel from '../tracking/ShipmentTrackingPanel';
import { shipmentService } from '../../services/shipmentService';
import { downloadBlob } from '../../utils/downloadBlob.js';
import { Package, Search, Eye, Download, Truck } from 'lucide-react';

const UserShipmentTracking = () => {
  const navigate = useNavigate();
  const [shipmentId, setShipmentId] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [returnId, setReturnId] = useState(null);
  const [loadingReturnId, setLoadingReturnId] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [deliveryImageModal, setDeliveryImageModal] = useState(false);
  const [selectedDeliveryImage, setSelectedDeliveryImage] = useState(null);
  const [deliveryImageTitle, setDeliveryImageTitle] = useState('Delivery Proof');

  useEffect(() => {
    if (trackingData?.shipment_details?.status?.toLowerCase() === 'returned') {
      fetchReturnId(trackingData.shipment_details.shipment_id);
    }
  }, [trackingData]);

  const fetchReturnId = async (id) => {
    try {
      setLoadingReturnId(true);
      const response = await axios.get('/shipments/returned', {
        params: { return_status: 'close' },
      });

      if (response.data.success && response.data.data?.length > 0) {
        const returns = response.data.data.filter((r) => r.shipment_id === id);
        if (returns.length > 0) {
          const sortedReturns = returns.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          setReturnId(sortedReturns[0].return_id);
        }
      }
    } catch (err) {
      console.error('Error fetching return ID:', err);
      setReturnId(null);
    } finally {
      setLoadingReturnId(false);
    }
  };

  const handleViewDeliveryImage = (imageUrl, title) => {
    setSelectedDeliveryImage(imageUrl);
    setDeliveryImageTitle(title || 'Delivery Proof');
    setDeliveryImageModal(true);
  };

  const handleTrack = async (e) => {
    e.preventDefault();

    if (!shipmentId.trim()) {
      setError('Please enter a consignment number');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);
    setReturnId(null);

    try {
      const response = await axios.get(`/tracking/${shipmentId}`);
      if (response.data.success) {
        setTrackingData(response.data);
      } else {
        setError(response.data.error || 'Shipment not found');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tracking information');
    } finally {
      setLoading(false);
    }
  };

  const handleViewShipment = () => {
    if (trackingData?.shipment_details?.shipment_id) {
      navigate(`/shipments/view/${trackingData.shipment_details.shipment_id}`);
    }
  };

  const handleDownloadInvoice = async () => {
    const id = trackingData?.shipment_details?.shipment_id;
    if (!id) return;
    try {
      setDownloadingInvoice(true);
      const response = await shipmentService.downloadInvoice(id);
      await downloadBlob(response.data, `invoice_${id}.pdf`, 'application/pdf');
      Alert.success('Invoice downloaded successfully');
    } catch (err) {
      console.error('Error downloading invoice:', err);
      Alert.error('Failed to download invoice');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleViewReturn = () => {
    if (returnId) {
      navigate(`/returns/view/${returnId}`);
    }
  };

  const isReturnedStatus = trackingData?.shipment_details?.status?.toLowerCase() === 'returned';

  const headerActions = trackingData ? (
    <>
      <Button variant="primary" icon={Eye} onClick={handleViewShipment} className="w-full sm:w-auto">
        View Shipment
      </Button>
      <Button
        variant="secondary"
        icon={Download}
        onClick={handleDownloadInvoice}
        loading={downloadingInvoice}
        disabled={downloadingInvoice}
        className="w-full sm:w-auto"
      >
        Download Invoice
      </Button>
      {isReturnedStatus && returnId && (
        <Button
          variant="danger"
          icon={Eye}
          onClick={handleViewReturn}
          loading={loadingReturnId}
          disabled={loadingReturnId}
          className="w-full sm:w-auto"
        >
          View Return
        </Button>
      )}
    </>
  ) : null;

  return (
    <DashboardLayout fullWidth>
      <div className="min-w-0">
        <div className="max-w-3xl mx-auto text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 leading-tight flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
            <span className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 text-purple-700 shrink-0">
              <Truck className="w-5 h-5 sm:w-6 sm:h-6" />
            </span>
            <span className="bg-gradient-to-r from-pink-800 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Track Your Shipment
            </span>
          </h1>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600 leading-relaxed px-1">
            Enter your consignment number for real-time delivery updates.
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-6 sm:mb-8 rounded-2xl p-4 sm:p-5 md:p-6 bg-white border border-purple-100 shadow-lg">
          <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1 min-w-0">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400 pointer-events-none" />
              <input
                type="text"
                value={shipmentId}
                onChange={(e) => setShipmentId(e.target.value)}
                placeholder="Consignment No"
                className="login-input w-full min-w-0 h-11 pl-10 pr-3 text-sm sm:text-base border border-purple-100 rounded-xl bg-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all placeholder:text-gray-400"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !String(shipmentId).trim()}
              className="w-full sm:w-auto sm:min-w-[120px] h-11 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600 text-white px-5 rounded-full font-semibold transition-all disabled:opacity-45 disabled:cursor-not-allowed shadow-sm text-sm sm:text-base shrink-0"
            >
              <Search className="w-4 h-4 shrink-0" />
              {loading ? 'Tracking...' : 'Track'}
            </button>
          </form>
        </div>

        <div className="w-full min-w-0">
        {loading && (
          <div className="bg-white rounded-2xl border border-purple-100 shadow-lg p-4">
            <Loader fullScreen={false} size="default" message="Tracking your shipment..." />
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-3 sm:p-4">
            <p className="text-red-800 font-medium text-sm sm:text-base break-words">{error}</p>
          </div>
        )}

        {!loading && trackingData && (
          <div className="rounded-2xl overflow-hidden border border-purple-100 shadow-lg bg-white">
            <ShipmentTrackingPanel
              trackingData={trackingData}
              onViewDeliveryImage={handleViewDeliveryImage}
              headerActions={headerActions}
            />
          </div>
        )}

        {!trackingData && !loading && !error && (
          <div className="bg-white rounded-2xl border border-purple-100 shadow-lg p-6 sm:p-8 md:p-10 text-center min-w-0">
            <div className="mx-auto mb-4 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
              <Package className="w-7 h-7 sm:w-8 sm:h-8 text-purple-500" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2 bg-gradient-to-r from-pink-800 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Track Your Shipment
            </h3>
            <p className="text-gray-600 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
              Enter a consignment number, then tap Track to view real-time status and route updates.
            </p>
          </div>
        )}
        </div>
      </div>

      <DocumentModal
        isOpen={deliveryImageModal}
        onClose={() => setDeliveryImageModal(false)}
        documentUrl={selectedDeliveryImage || ''}
        documentType="image"
        title={deliveryImageTitle}
      />
    </DashboardLayout>
  );
};

export default UserShipmentTracking;
